'use client';

// ---------------------------------------------------------------------------
// MusicScheduleRunner — mounts the schedule clock
// ---------------------------------------------------------------------------
//
// Renders nothing. It exists so `useMusicSchedule` is running whenever the app
// is open, not only while the player panel happens to be on screen — a schedule
// that only fired with the panel open would do nothing on a front desk where
// nobody has opened it.
//
// Mounted from MusicPlayerHost's parent (MusicProvider), which lives in
// providers.tsx and never remounts. If it were inside the panel, crossing a route
// group or closing the panel would cancel the armed wake-up.
//
// A platform admin has no property, so the schedules endpoint 400s for them. The
// hook's query has `retry: false` and this component tolerates the failure —
// there is simply nothing to schedule.
// ---------------------------------------------------------------------------

import { useCallback } from 'react';

import { api } from '@/core/lib/api-client';

import { useMusicSchedule } from '../hooks/useMusicSchedule';

interface PlaylistDetail {
  data: {
    id: string;
    items: {
      position: number;
      trackId: string | null;
      localRef: string | null;
      localLabel: string | null;
    }[];
  };
}

export function MusicScheduleRunner() {
  /**
   * Turn a playlist into the ids the queue plays, in order.
   *
   * Local references are kept. They resolve only on the device holding the file
   * — `resolveTrackSource` returns 'missing' elsewhere and the queue skips past
   * it — so dropping them here would silently change what a manager built into
   * something different on every other machine.
   */
  const loadPlaylistTrackIds = useCallback(async (playlistId: string): Promise<string[]> => {
    try {
      const res = await api.get<PlaylistDetail>(`/hotel/music/playlists/${playlistId}`);
      return [...res.data.items]
        .sort((a, b) => a.position - b.position)
        .map((item) => item.trackId ?? item.localRef)
        .filter((id): id is string => Boolean(id));
    } catch {
      // The playlist was deleted, or the network is down. Returning nothing
      // means the schedule does not start, which is the right outcome — better
      // than playing something unintended.
      return [];
    }
  }, []);

  useMusicSchedule(loadPlaylistTrackIds);

  return null;
}
